import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TextArea } from '@progress/kendo-react-inputs'
import GhostTemplateEditor from '../components/common/GhostTemplateEditor'
import {
  NarrativeOcgEditor,
  SelectContactTooltipLight,
} from '@features/narrativeOcg'
import { useDeviceStore } from '@stores/useDeviceStore'

export const DescriptionEditorInline = ({
  value,
  onChange,
  item,
  defaultIsEditing = false,
  isMobile = false,
  showTemplateShortcutHint = false,
  validationHint: externalValidationHint,
  onAcceptValidation,
  onDiscardValidation,
  onRevertValidation,
  validationActionLoading,
  onFloatingWindowInteraction,
  onNarrativeInteraction,
  onNarrativeOutsideClick,
  validateOnBlur = false,
  dialogActions = false,
  footerPanel = /** @type {import('react').ReactNode} */ (null),
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(defaultIsEditing)
  const [inputValue, setInputValue] = useState(value || '')
  const [validationHint, setValidationHint] = useState({
    visible: false,
    keyword: '',
    reason: '',
  })
  const ghostEditorRef = useRef(null)
  const containerRef = useRef(null)
  const effectiveValidationHint = externalValidationHint || validationHint
  const isDeviceMobile = useDeviceStore(state => state.isMobile)
  const effectiveIsMobile = isMobile || isDeviceMobile

  const settings = JSON.parse(sessionStorage.getItem('settings') || '[]')
  const templateNarrative = settings.find(s => s.name === 'TEMPLATE_NARRATIVE')
  const useTemplateNarrative = Boolean(
    templateNarrative?.value?.ENABLED && !effectiveIsMobile
  )

  useEffect(() => {
    const nextValue = value || ''
    setInputValue(currentValue =>
      currentValue === nextValue ? currentValue : nextValue
    )
  }, [value])

  const getLatestNarrativeValue = useCallback(
    (options = {}) => {
      const { emit = true } = options
      if (useTemplateNarrative) {
        if (emit) {
          return ghostEditorRef.current?.flush?.() ?? inputValue
        }

        return ghostEditorRef.current?.getValue?.() ?? inputValue
      }

      return inputValue
    },
    [inputValue, useTemplateNarrative]
  )

  useEffect(() => {
    if (!onNarrativeOutsideClick) return undefined

    const handlePointerDown = event => {
      const container = containerRef.current
      const target = event.target

      if (!container || !(target instanceof Node)) return
      if (container.contains(target)) {
        onNarrativeInteraction?.(event)
        return
      }

      onNarrativeOutsideClick(
        event,
        getLatestNarrativeValue({ emit: !effectiveValidationHint?.visible })
      )
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [
    effectiveValidationHint?.visible,
    getLatestNarrativeValue,
    onNarrativeInteraction,
    onNarrativeOutsideClick,
  ])

  const handleChange = nextValue => {
    setInputValue(nextValue)
    onChange?.(nextValue)
  }

  const handleClear = event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    handleChange('')
    ghostEditorRef.current?.clear?.()
  }

  return (
    <div
      ref={containerRef}
      aria-disabled={disabled}
      className={`description-editor-container relative ${disabled ? 'pointer-events-none' : ''}`}
      onFocusCapture={onNarrativeInteraction}
    >
      <NarrativeOcgEditor
        isMobile={effectiveIsMobile}
        validationHint={effectiveValidationHint}
        onAcceptValidation={onAcceptValidation}
        onDiscardValidation={onDiscardValidation}
        onRevertValidation={onRevertValidation}
        validationActionLoading={validationActionLoading}
        onFloatingWindowInteraction={onFloatingWindowInteraction}
        dialogActions={dialogActions}
        footerPanel={footerPanel}
        toolbarStart={
          !effectiveIsMobile ? <SelectContactTooltipLight /> : null
        }
        toolbarEnd={
          <button
            aria-label="Clear narrative"
            className="trashcan-icon cursor-pointer border-0 p-0"
            onClick={handleClear}
            title="Clear narrative"
            type="button"
          />
        }
      >
        <div className="relative">
          {useTemplateNarrative ? (
            <GhostTemplateEditor
              ref={ghostEditorRef}
              item={item}
              isFormInline={true}
              autoFocus={isEditing}
              setIsEditing={setIsEditing}
              defaultValue={inputValue}
              isMobile={effectiveIsMobile}
              showTemplateShortcutHint={showTemplateShortcutHint}
              validateOnBlur={validateOnBlur}
              disabled={disabled}
              onChange={event => handleChange(event.value)}
              onValidationChange={payload => setValidationHint(payload)}
            />
          ) : (
            <TextArea
              disabled={disabled}
              autoFocus={isEditing}
              value={inputValue}
              onChange={event => handleChange(event.value)}
              rows={4}
              className="box-border min-h-40 w-full rounded border-0 p-0 text-sm font-normal leading-[1.42] tracking-[0.25px] outline-none"
              style={{
                border: 'none',
                outline: 'none',
                padding: 0,
                fontFamily: 'inherit',
                fontSize: effectiveIsMobile ? 16 : 14,
                lineHeight: effectiveIsMobile ? 1.5 : 1.42,
              }}
            />
          )}
        </div>
      </NarrativeOcgEditor>
    </div>
  )
}
